import { scrollMonitorServiceModule } from '../../services/scroll-monitor-service/scroll-monitor-service';
import { uiBootstrapPositionModule } from '../../vendor/ui-bootstrap-position-module';

export const positionHelperModule = angular
  .module('Q.helpers.position-helper', [
    uiBootstrapPositionModule,
    scrollMonitorServiceModule,
  ])

  .factory(
    'positionHelper',
    function ($window, $document, $position, scrollMonitorService, $timeout) {
      /**
       * Positions the popup around the element once
       *
       * @param element {Node} Element to position around
       * @param popup {Node} Popup element
       * @param position {String} position property
       * @param hasArrow {boolean} If true, align the nested arrow as well
       * @param setMinWidth {boolean} If true, set the min width to the element width
       * @param mousePosition {Object} If provided, the popup is oriented around the {pageX,pageY} properties
       */
      function positionPopup(
        element,
        popup,
        position = 'bottom',
        { hasArrow = false, setMinWidth = false, mousePosition = null } = {},
      ) {
        // don't move the popup if the element isn't attached
        if (!element.isVisible()) return;

        const popupPosition = $position.positionElements(
          element,
          popup,
          position,
          true,
        );

        // params for making sure the node stays on-screen
        const horizontal = /^left/.test(position) || /^right/.test(position);
        const vertical = /^bottom/.test(position) || /^top/.test(position);
        const $body = angular.element($document[0].body);
        const $win = angular.element($window);
        const winWidth = Math.min($body.offsetWidth(), $win.offsetWidth());
        const winHeight = Math.max($body.offsetHeight(), $win.offsetHeight());
        const popupWidth = popup.offsetWidth();
        const popupHeight = popup.offsetHeight();
        const arrowOffsetAdjustment = 18;
        const arrowAdjustments = { x: 0, y: 0 };

        if (mousePosition) {
          const elBounds = element[0].getBoundingClientRect();
          if (vertical) {
            // top and bottom are horizontally centered
            popupPosition.left +=
              mousePosition.pageX - (elBounds.left + elBounds.width / 2);
          } else if (position === 'right') {
            popupPosition.left -= elBounds.right - mousePosition.pageX;
          } else {
            popupPosition.left += mousePosition.pageX - elBounds.left;
          }
          if (horizontal) {
            // left and right are vertically centered
            popupPosition.top +=
              mousePosition.pageY - (elBounds.top + elBounds.height / 2);
          } else if (position === 'top') {
            popupPosition.top -= elBounds.top - mousePosition.pageY;
          } else {
            popupPosition.top += elBounds.top - mousePosition.pageY;
          }
        }

        popupPosition['margin-left'] = null;
        popupPosition['margin-right'] = null;
        popupPosition['margin-top'] = null;
        popupPosition['margin-bottom'] = null;

        if (setMinWidth) {
          popupPosition['min-width'] = element.offsetWidth() + 'px';
        }

        // ensure we're not off the right edge
        if (popupPosition.left + popupWidth > winWidth - 2) {
          let finalLeft = winWidth - 2 - popupWidth;
          if (vertical && hasArrow) {
            arrowAdjustments.x = Math.min(
              (popupWidth - arrowOffsetAdjustment) / 2,
              popupPosition.left - finalLeft,
            );
          }
          popupPosition['margin-right'] = '0px';
          popupPosition.left = finalLeft;
        }
        // ensure we're not off the left edge (overrides right side)
        if (popupPosition.left < 2) {
          if (vertical && hasArrow) {
            arrowAdjustments.x = Math.max(
              (arrowOffsetAdjustment - popupWidth) / 2,
              popupPosition.left - 2,
            );
          }
          popupPosition['margin-left'] = '0px';
          popupPosition.left = 2;
        }
        // ensure we're not off the bottom edge
        if (popupPosition.top + popupHeight > winHeight - 2) {
          let finalTop = winHeight - 2 - popupHeight;
          if (horizontal && hasArrow) {
            arrowAdjustments.y = Math.min(
              (popupHeight - arrowOffsetAdjustment) / 2,
              popupPosition.top - finalTop,
            );
          }
          popupPosition['margin-top'] = '0px';
          popupPosition.top = finalTop;
        }
        // ensure we're not off the top edge (overrides bottom side)
        if (popupPosition.top < 2) {
          if (horizontal && hasArrow) {
            arrowAdjustments.y = Math.max(
              (arrowOffsetAdjustment - popupHeight) / 2,
              popupPosition.top - 2,
            );
          }
          popupPosition['margin-bottom'] = '0px';
          popupPosition.top = 2;
        }

        // final styling changes
        popupPosition.top += 'px';
        popupPosition.left += 'px';

        popup.css(popupPosition);
        // offset arrow if necessary
        if (hasArrow) {
          popup.querySelector('.arrow').css({
            transform: `translate(${arrowAdjustments.x}px, ${arrowAdjustments.y}px)`,
          });
        }
      }

      /**
       * Automatically positions the popup near the element, and watches to ensure it stays in place.
       *
       * @param $scope Scope for listening on
       * @param element Element to position around
       * @param popup Popup element
       * @param position position property
       * @param opts Options passed to the positionPopup function
       * @returns {function()} Deregistration function, only necessary if the popup is closed before the scope is destroyed
       */
      function autoPositionPopup($scope, element, popup, position, opts) {
        popup.css({ opacity: 0 });

        function setPosition() {
          $timeout(
            () => {
              positionPopup(element, popup, position, opts);
              popup.css({ opacity: null });
            },
            0,
            false,
          );
        }

        setPosition();

        if (opts.mousePosition) {
          // only position once if we're using mouse position
          return angular.noop;
        }

        const scrollMonitor = scrollMonitorService.addListener(
          element.scrollableParent(),
          setPosition,
        );
        const unlistenDestroy = $scope.$on('$destroy', () =>
          scrollMonitor.destroy(),
        );
        const unlistenWatch = $scope.$watch(setPosition);

        return () => {
          scrollMonitor.destroy();
          unlistenDestroy();
          unlistenWatch();
        };
      }

      function topOrBottom(el, estimatedHeight) {
        let position = 'bottom';
        // if within 200px (or 1/3) of the bottom, switch to top
        const elOffset = el.offset();
        const parent = el.scrollableParent();
        const parentOffset = parent.offset();
        const variance = Math.max(
          estimatedHeight || 0,
          Math.min(200, parentOffset.height / 3),
        );
        if (
          elOffset.bottom - parent.scrollTop() >
          parentOffset.bottom - variance
        ) {
          position = 'top';
        }
        return position;
      }

      return {
        autoPositionPopup,
        positionPopup,
        topOrBottom,
      };
    },
  );
